import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface ColumnSettings {
  show_order_name?: boolean;
  show_delivery_date?: boolean;
  show_client_name?: boolean;
  show_quantity?: boolean;
  show_status?: boolean;
  show_value?: boolean;
  show_offer_number?: boolean;
  show_valid_until?: boolean;
  show_created_date?: boolean;
  show_machine?: boolean;
  show_operator?: boolean;
  show_start_time?: boolean;
  show_end_time?: boolean;
  default_sort_column?: string;
  default_sort_direction?: 'asc' | 'desc';
}

interface AllColumnSettings {
  orders: ColumnSettings;
  offers: ColumnSettings;
  production: ColumnSettings;
}

export const useColumnSettings = () => {
  const [columnSettings, setColumnSettings] = useState<AllColumnSettings>({
    orders: {
      show_order_name: true,
      show_delivery_date: true,
      show_client_name: true,
      show_quantity: true,
      show_status: true,
      show_value: true,
      default_sort_column: 'created_at',
      default_sort_direction: 'desc'
    },
    offers: {
      show_offer_number: true,
      show_client_name: true,
      show_valid_until: true,
      show_status: true,
      show_value: true,
      show_created_date: true,
      default_sort_column: 'created_at',
      default_sort_direction: 'desc'
    },
    production: {
      show_order_name: true,
      show_machine: true,
      show_operator: true,
      show_start_time: true,
      show_end_time: true,
      show_status: true,
      default_sort_column: 'start_time',
      default_sort_direction: 'desc'
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stały UUID dla domyślnego użytkownika
  const userId = '00000000-0000-0000-0000-000000000001';

  // Pobierz ustawienia z bazy danych
  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('user_settings')
        .select('column_settings')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data?.column_settings) {
        setColumnSettings(data.column_settings as AllColumnSettings);
      } else {
        // Jeśli nie ma rekordu, utwórz go z domyślnymi wartościami
        await createDefaultSettings();
      }
    } catch (err: any) {
      console.error('Error fetching column settings:', err);
      setError(err.message);
      // W przypadku błędu użyj lokalnych ustawień
      loadLocalSettings();
    } finally {
      setLoading(false);
    }
  };

  // Utwórz domyślne ustawienia w bazie
  const createDefaultSettings = async () => {
    try {
      const { error } = await supabase
        .from('user_settings')
        .insert({
          user_id: userId,
          column_settings: columnSettings
        });

      if (error && error.code !== '23505') { // Ignoruj błąd duplikatu
        throw error;
      }
    } catch (err: any) {
      console.error('Error creating default settings:', err);
    }
  };

  // Zapisz ustawienia do bazy danych
  const saveSettings = async (view: keyof AllColumnSettings, settings: ColumnSettings) => {
    try {
      // Zaktualizuj lokalny stan
      const newSettings = {
        ...columnSettings,
        [view]: settings
      };
      setColumnSettings(newSettings);

      // Zapisz do localStorage jako backup
      localStorage.setItem('plexisystem_column_settings', JSON.stringify(newSettings));

      // Zapisz do bazy danych
      const { error } = await supabase
        .from('user_settings')
        .update({
          column_settings: newSettings
        })
        .eq('user_id', userId);

      if (error) throw error;

      return { success: true };
    } catch (err: any) {
      console.error('Error saving column settings:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Załaduj ustawienia z localStorage
  const loadLocalSettings = () => {
    try {
      const saved = localStorage.getItem('plexisystem_column_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        setColumnSettings(parsed);
      }
    } catch (err) {
      console.error('Error loading local settings:', err);
    }
  };

  // Resetuj ustawienia do domyślnych
  const resetSettings = async (view?: keyof AllColumnSettings) => {
    try {
      const defaultSettings: AllColumnSettings = {
        orders: {
          show_order_name: true,
          show_delivery_date: true,
          show_client_name: true,
          show_quantity: true,
          show_status: true,
          show_value: true,
          default_sort_column: 'created_at',
          default_sort_direction: 'desc'
        },
        offers: {
          show_offer_number: true,
          show_client_name: true,
          show_valid_until: true,
          show_status: true,
          show_value: true,
          show_created_date: true,
          default_sort_column: 'created_at',
          default_sort_direction: 'desc'
        },
        production: {
          show_order_name: true,
          show_machine: true,
          show_operator: true,
          show_start_time: true,
          show_end_time: true,
          show_status: true,
          default_sort_column: 'start_time',
          default_sort_direction: 'desc'
        }
      };

      const newSettings = view 
        ? { ...columnSettings, [view]: defaultSettings[view] }
        : defaultSettings;

      setColumnSettings(newSettings);
      localStorage.setItem('plexisystem_column_settings', JSON.stringify(newSettings));

      const { error } = await supabase
        .from('user_settings')
        .update({
          column_settings: newSettings
        })
        .eq('user_id', userId);

      if (error) throw error;

      return { success: true };
    } catch (err: any) {
      console.error('Error resetting settings:', err);
      return { success: false, error: err.message };
    }
  };

  // Pobierz ustawienia dla konkretnego widoku
  const getViewSettings = (view: keyof AllColumnSettings): ColumnSettings => {
    return columnSettings[view] || {};
  };

  // Hook effect do załadowania ustawień przy starcie
  useEffect(() => {
    fetchSettings();
  }, []);

  // Nasłuchuj na zmiany w innych kartach
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'plexisystem_column_settings' && e.newValue) {
        try {
          const newSettings = JSON.parse(e.newValue);
          setColumnSettings(newSettings);
        } catch (err) {
          console.error('Error parsing storage change:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    columnSettings,
    loading,
    error,
    saveSettings,
    resetSettings,
    getViewSettings,
    refreshSettings: fetchSettings
  };
};
